import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import Toggle from '@cloudscape-design/components/toggle';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import TokenGroup from '@cloudscape-design/components/token-group';
import Tabs from '@cloudscape-design/components/tabs';
import CodeEditor from '@cloudscape-design/components/textarea';

const FIELD_TYPES: SelectProps.Option[] = [
  { label: 'String', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Object', value: 'object' },
  { label: 'Array', value: 'array' },
];

export interface SchemaField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
  minLength?: number;
  minimum?: number;
  items?: { type: string };
}

interface SchemaBuilderProps {
  onSchemaGenerated: (schema: object) => void;
  initialSchema?: object;
}

export function SchemaBuilder({ onSchemaGenerated, initialSchema }: SchemaBuilderProps) {
  const [fields, setFields] = useState<SchemaField[]>(() => {
    if (initialSchema) return parseSchemaToFields(initialSchema);
    return [];
  });
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<SelectProps.Option>(FIELD_TYPES[0]);
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [activeTab, setActiveTab] = useState('visual');
  const [rawJson, setRawJson] = useState(() => JSON.stringify(fieldsToSchema(fields), null, 2));

  const addField = () => {
    if (!newFieldName.trim()) return;
    const field: SchemaField = {
      name: newFieldName.trim(),
      type: newFieldType.value!,
      required: newFieldRequired,
    };
    const updated = [...fields, field];
    setFields(updated);
    onSchemaGenerated(fieldsToSchema(updated));
    setNewFieldName('');
    setNewFieldRequired(true);
  };

  const removeField = (index: number) => {
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    onSchemaGenerated(fieldsToSchema(updated));
  };

  const toggleRequired = (index: number) => {
    const updated = fields.map((f, i) => i === index ? { ...f, required: !f.required } : f);
    setFields(updated);
    onSchemaGenerated(fieldsToSchema(updated));
  };

  const generatedSchema = fieldsToSchema(fields);

  return (
    <Tabs
      activeTabId={activeTab}
      onChange={({ detail }) => {
        if (detail.activeTabId === 'json') setRawJson(JSON.stringify(fieldsToSchema(fields), null, 2));
        setActiveTab(detail.activeTabId);
      }}
      tabs={[
        {
          id: 'visual',
          label: 'Visual editor',
          content: (
            <SpaceBetween size="m">
              {fields.length > 0 && (
                <Table
                  items={fields}
                  columnDefinitions={[
                    { id: 'name', header: 'Field', cell: (item) => <code>{item.name}</code> },
                    { id: 'type', header: 'Type', cell: (item) => item.type },
                    { id: 'required', header: 'Required', cell: (item) => (
                      <Toggle checked={item.required} onChange={() => toggleRequired(fields.indexOf(item))}>
                        {item.required ? 'Yes' : 'No'}
                      </Toggle>
                    )},
                    { id: 'actions', header: '', cell: (item) => (
                      <Button variant="icon" iconName="remove" onClick={() => removeField(fields.indexOf(item))} />
                    )},
                  ]}
                  variant="embedded"
                  empty={<Box textAlign="center" color="text-body-secondary">No fields defined</Box>}
                />
              )}

              <Container>
                <ColumnLayout columns={4}>
                  <FormField label="Field name">
                    <Input
                      value={newFieldName}
                      onChange={({ detail }) => setNewFieldName(detail.value)}
                      placeholder="orderId"
                      onKeyDown={({ detail }) => { if (detail.key === 'Enter') addField(); }}
                    />
                  </FormField>
                  <FormField label="Type">
                    <Select
                      selectedOption={newFieldType}
                      onChange={({ detail }) => setNewFieldType(detail.selectedOption)}
                      options={FIELD_TYPES}
                    />
                  </FormField>
                  <FormField label="Required">
                    <Toggle checked={newFieldRequired} onChange={({ detail }) => setNewFieldRequired(detail.checked)}>
                      {newFieldRequired ? 'Yes' : 'No'}
                    </Toggle>
                  </FormField>
                  <FormField label=" ">
                    <Button onClick={addField} disabled={!newFieldName.trim()}>Add field</Button>
                  </FormField>
                </ColumnLayout>
              </Container>

              {fields.length > 0 && (
                <ExpandableSection headerText="Generated JSON Schema" variant="footer">
                  <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                    {JSON.stringify(generatedSchema, null, 2)}
                  </pre>
                </ExpandableSection>
              )}
            </SpaceBetween>
          ),
        },
        {
          id: 'json',
          label: 'JSON',
          content: (
            <FormField label="JSON Schema" description="Paste or edit raw JSON Schema directly.">
              <CodeEditor
                value={rawJson}
                onChange={({ detail }) => {
                  setRawJson(detail.value);
                  try {
                    const parsed = JSON.parse(detail.value);
                    onSchemaGenerated(parsed);
                    setFields(parseSchemaToFields(parsed));
                  } catch {}
                }}
                rows={15}
              />
            </FormField>
          ),
        },
      ]}
    />
  );
}

export function fieldsToSchema(fields: SchemaField[]): object {
  const properties: Record<string, object> = {};
  const required: string[] = [];

  for (const field of fields) {
    const prop: Record<string, unknown> = { type: field.type };
    if (field.type === 'string' && field.minLength) prop.minLength = field.minLength;
    if (field.type === 'number' && field.minimum !== undefined) prop.minimum = field.minimum;
    if (field.enum) prop.enum = field.enum;
    if (field.type === 'array' && field.items) prop.items = field.items;
    properties[field.name] = prop;
    if (field.required) required.push(field.name);
  }

  return {
    type: 'object',
    ...(required.length > 0 && { required }),
    properties,
  };
}

export function parseSchemaToFields(schema: any): SchemaField[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required || []);
  return Object.entries(schema.properties).map(([name, prop]: [string, any]) => ({
    name,
    type: prop.type || 'string',
    required: required.has(name),
    enum: prop.enum,
    minLength: prop.minLength,
    minimum: prop.minimum,
    items: prop.items,
  }));
}
